/* eslint-disable @typescript-eslint/no-explicit-any */
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { FLUSH, PAUSE, PERSIST, PersistConfig, persistStore, PURGE, REGISTER, REHYDRATE, createTransform } from "redux-persist";
import storage from "redux-persist/lib/storage";
import persistReducer from "redux-persist/lib/persistReducer";
import userAccountReducer from "./slices/userAccountSlice"
import globalReducer from "./slices/globalSlice";
import liveChatReducer from "./slices/liveChatSlice";
import ResourceReducer, { initialState as resourceInitialState } from "./slices/resourceSlice";
import quoteWidgetReducer from "./slices/quoteWidgetSlice";
import { PERSISTENT_STORAGE_KEY } from "../resources/constants";

// Transform for the resource slice to handle ENUMS properly
const resourceTransform = createTransform(
  // transform state on its way to being serialized
  (inboundState: any) => {
    // Make sure we're not trying to persist any non-serializable data
    const { ENUMS, ...rest } = inboundState;
    return {
      ...rest,
      ENUMS: ENUMS ? {
        protectionTypes: Array.isArray(ENUMS.protectionTypes) ? ENUMS.protectionTypes : [],
        vehicleUsages: Array.isArray(ENUMS.vehicleUsages) ? ENUMS.vehicleUsages : [],
        idTypes: Array.isArray(ENUMS.idTypes) ? ENUMS.idTypes : [],
        usageGroups: Array.isArray(ENUMS.usageGroups) ? ENUMS.usageGroups : [],
        companyTypes: Array.isArray(ENUMS.companyTypes) ? ENUMS.companyTypes : [],
        accountTypes: Array.isArray(ENUMS.accountTypes) ? ENUMS.accountTypes : [],
        accountProviders: Array.isArray(ENUMS.accountProviders) ? ENUMS.accountProviders : [],
        genders: Array.isArray(ENUMS.genders) ? ENUMS.genders : [],
        employmentTypes: Array.isArray(ENUMS.employmentTypes) ? ENUMS.employmentTypes : [],
        policyTypes: Array.isArray(ENUMS.policyTypes) ? ENUMS.policyTypes : [],
        installmentPeriods: Array.isArray(ENUMS.installmentPeriods) ? ENUMS.installmentPeriods : [],
        paymentStatuses: Array.isArray(ENUMS.paymentStatuses) ? ENUMS.paymentStatuses : [],
        reviewStages: Array.isArray(ENUMS.reviewStages) ? ENUMS.reviewStages : [],
        fuelSaleStatuses: Array.isArray(ENUMS.fuelSaleStatuses) ? ENUMS.fuelSaleStatuses : [],
        currencyCodes: Array.isArray(ENUMS.currencyCodes) ? ENUMS.currencyCodes : [],
        bodyTypes: Array.isArray(ENUMS.bodyTypes) ? ENUMS.bodyTypes : [],
        vehicleBodyTypes: Array.isArray(ENUMS.vehicleBodyTypes) ? ENUMS.vehicleBodyTypes : [],
        
      } : { ...resourceInitialState.ENUMS }
    };
  },
  // transform state being rehydrated
  (outboundState: any) => {
    // Ensure all enum arrays exist when rehydrating
    return {
      ...outboundState,
      ENUMS: {
        ...resourceInitialState.ENUMS,
        ...(outboundState.ENUMS || {})
      }
    };
  },
  { whitelist: ['resource'] }
);

const persistConfig: PersistConfig<any, any, any, any> = {
  key: PERSISTENT_STORAGE_KEY,
  version: 3,
  storage,
  throttle: 1000,
  serialize: true,
  transforms: [resourceTransform],
  // Handle version migrations
  migrate: (state: any) => {
    // If state exists but doesn't have ENUMS, initialize it
    if (state && state.resource && !state.resource.ENUMS) {
      state.resource = {
        ...state.resource,
        ENUMS: { ...resourceInitialState.ENUMS }
      };
    }
    return Promise.resolve(state);
  }
};

const rootReducer = combineReducers({
  userAccount: userAccountReducer,
  globals: globalReducer,
  livechat: liveChatReducer,
  resource: ResourceReducer,
  quoteWidget: quoteWidgetReducer
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});


export default store;
export const persistor = persistStore(store);
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;